import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw } from "lucide-react";
import Navigation from "@/components/site/Navigation";
import Footer from "@/components/site/Footer";
import { fetchSummary, fetchMoods } from "@/lib/analytics";
import { CASE_STUDIES, JOURNEY } from "@/data/portfolio";
import { MOOD_ICONS } from "@/components/site/MoodVote";

const SECTIONS = [
  ["hero", "Hero"],
  ["journey", "Journey map"],
  ["live-stats", "Live stats"],
  ["about", "About"],
  ["experience", "Experience"],
  ["projects", "Projects"],
  ["deliverables", "Deliverables"],
  ["ai-methodology", "AI methodology"],
  ["toolkit", "Toolkit"],
  ["personality", "Personality"],
  ["testimonials", "Testimonials"],
  ["contact", "Contact"],
];

const REFRESH_MS = 30000;

export default function Analytics() {
  const [data, setData] = useState(null);
  const [moods, setMoods] = useState({});
  const [err, setErr] = useState(null);

  const load = () =>
    fetchMoods().then(setMoods).catch(() => {}) &&
    fetchSummary()
      .then((d) => {
        setData(d);
        setErr(null);
      })
      .catch((e) => setErr(e.message));

  useEffect(() => {
    document.title = "Portfolio analytics, Sinthu Nathan";
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  const t = data?.totals;

  return (
    <>
      <Navigation />
      <main data-testid="analytics-page" className="bg-[#faf8f5]">
        <section className="py-14 lg:py-20 border-b border-[#e5e0d8]">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16">
            <Link
              to="/"
              data-testid="analytics-back"
              className="inline-flex items-center gap-2 text-sm text-[#55625b] hover:text-[#9a7218]"
            >
              <ArrowLeft className="h-4 w-4" /> Back to portfolio
            </Link>
            <div className="mt-6 flex items-center gap-3 font-mono-label text-[11px] text-[#9a7218]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#9a7218] pulse-dot" />
              Live · refreshes every 30s
            </div>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#14352a] max-w-4xl">
              A researcher should measure her own work.
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-[#55625b] max-w-3xl leading-relaxed">
              This portfolio instruments itself, page views, how far people
              scroll, which case studies get read, what visitors ask the AI
              concierge. First-party, cookieless, anonymous session IDs. It is
              the same loop I ran on the Sip &amp; Serve app: see the data, then
              decide.
            </p>
            {data?.since && (
              <div className="mt-4 font-mono-label text-[10px] text-[#86928a]">
                Tracking since {new Date(data.since).toLocaleDateString()} ·
                updated {new Date(data.generated_at).toLocaleTimeString()}
                <button
                  data-testid="analytics-refresh-btn"
                  onClick={load}
                  className="ml-3 inline-flex items-center gap-1 text-[#9a7218] link-underline"
                >
                  <RefreshCw className="h-3 w-3" /> refresh
                </button>
              </div>
            )}
            {err && (
              <p data-testid="analytics-error" className="mt-6 text-red-600">
                {err}
              </p>
            )}

            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
              <Kpi k="visits" label="Visits (sessions)" v={t?.visits} />
              <Kpi k="unique-visitors" label="Unique visitors" v={t?.unique_visitors} />
              <Kpi k="page-views" label="Page views" v={t?.page_views} />
              <Kpi k="case-study-reads" label="Case-study reads" v={t?.case_study_reads} />
              <Kpi k="concierge-conversations" label="Concierge conversations" v={t?.concierge_conversations} />
              <Kpi k="concierge-messages" label="Concierge messages" v={t?.concierge_messages} />
              <Kpi k="cv-downloads" label="CV downloads" v={t?.cv_downloads} />
              <Kpi k="contact-submits" label="Contact messages" v={t?.contact_submits} />
            </div>
          </div>
        </section>

        <section className="py-14 lg:py-20 border-b border-[#e5e0d8]">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <Label n="01" title="Traffic, last 14 days" />
              <DailyChart daily={data?.daily || []} />
            </div>
            <div className="lg:col-span-5">
              <Label n="02" title="Scroll reach, home page" sub="Unique sessions that reached each section." />
              <Bars
                testid="section"
                rows={SECTIONS.map(([id, label]) => ({
                  key: label,
                  count: data?.sections?.[id] || 0,
                }))}
                max={data?.sections?.hero || 1}
              />
            </div>
          </div>
        </section>

        <section className="py-14 lg:py-20 border-b border-[#e5e0d8] bg-[#f2ece4]">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <Label n="03" title="Which case studies get read" />
              <Bars
                testid="case-study"
                rows={CASE_STUDIES.map((cs) => ({
                  key: cs.label,
                  count:
                    data?.case_studies?.find((c) => c.key === cs.slug)?.count || 0,
                }))}
              />
            </div>
            <div className="lg:col-span-7">
              <Label n="04" title="What visitors ask the AI concierge" sub="Most frequent questions, verbatim." />
              <ol data-testid="concierge-questions" className="mt-6 space-y-3">
                {(data?.concierge_questions || []).length === 0 && (
                  <li className="text-sm text-[#86928a]">No conversations yet.</li>
                )}
                {(data?.concierge_questions || []).map((q, i) => (
                  <li
                    key={i}
                    className="flex gap-4 items-baseline bg-white border border-[#e5e0d8] px-5 py-3"
                  >
                    <span className="font-display text-lg text-[#9a7218] w-8 shrink-0">
                      {q.count}×
                    </span>
                    <span className="text-sm text-[#14352a] leading-relaxed">
                      &ldquo;{q.key}&rdquo;
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section className="py-14 lg:py-20 border-b border-[#e5e0d8]">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16">
            <Label n="05" title="Journey sentiment, live hiring-manager mood" sub="Visitors tap a smiley inside each journey stage. 1 = not convinced, 5 = hire her." />
            <MoodTable moods={moods} />
          </div>
        </section>

        <section className="py-14 lg:py-20">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <Label n="06" title="Method note" />
            </div>
            <div className="lg:col-span-8 text-lg text-[#14352a] leading-relaxed border-t lg:border-t-0 lg:border-l border-[#e5e0d8] pt-4 lg:pt-0 lg:pl-8">
              Events are first-party (no third-party scripts), keyed on an
              anonymous session ID and a visitor ID kept in the browser. Section
              reach uses an intersection observer at 35% visibility, once per
              section per session. This page and the admin inbox are excluded
              from counts. Small numbers are shown as small numbers, no
              smoothing, no extrapolation.
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

function MoodTable({ moods }) {
  return (
    <div data-testid="mood-table" className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
      {JOURNEY.map((s, i) => {
        const m = moods[s.id];
        const Icon = MOOD_ICONS[m ? Math.round(m.avg) - 1 : 2];
        return (
          <div key={s.id} data-testid={`mood-stage-${s.id}`} className="p-4 bg-white border border-[#e5e0d8]">
            <div className="font-mono-label text-[8px] text-[#9a7218]">{i + 1} · {s.stage}</div>
            <div className="mt-2 flex items-center gap-2">
              <Icon className={`h-5 w-5 ${m ? "text-[#d9a92e]" : "text-[#d4cebe]"}`} />
              <span className="font-display text-2xl tabular-nums">{m ? m.avg.toFixed(1) : ", "}</span>
              <span className="font-mono-label text-[8px] text-[#86928a]">/ 5</span>
            </div>
            <div className="mt-1 font-mono-label text-[8px] text-[#86928a]">{m ? `${m.count} vote${m.count === 1 ? "" : "s"}` : "no votes yet"}</div>
            {m && (
              <div className="mt-2 flex gap-0.5 h-1.5">
                {m.dist.map((d, j) => (
                  <span key={j} className="bg-[#e5e0d8] flex-1 relative overflow-hidden">
                    <span className="absolute inset-0 bg-[#9a7218]" style={{ transform: `scaleY(${d / m.count})`, transformOrigin: "bottom" }} />
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Kpi({ k, label, v }) {
  return (
    <div data-testid={`kpi-${k}`} className="border-t border-[#d4cebe] pt-3">
      <div className="font-display text-4xl text-[#14352a] tabular-nums">
        {v ?? ", "}
      </div>
      <div className="font-mono-label text-[10px] text-[#86928a] leading-snug mt-1">
        {label}
      </div>
    </div>
  );
}

function Label({ n, title, sub }) {
  return (
    <div>
      <div className="font-mono-label text-[10px] text-[#9a7218]">{n}</div>
      <h2 className="mt-3 font-display text-2xl leading-tight text-[#14352a]">
        {title}
      </h2>
      {sub && <p className="mt-2 text-sm text-[#55625b]">{sub}</p>}
    </div>
  );
}

function Bars({ rows, max, testid }) {
  const m = Math.max(max || 0, ...rows.map((r) => r.count), 1);
  return (
    <ul data-testid={`bars-${testid}`} className="mt-6 space-y-3">
      {rows.map((r) => (
        <li key={r.key} className="grid grid-cols-[120px_1fr_40px] items-center gap-3 text-sm">
          <span className="text-[#14352a] truncate">{r.key}</span>
          <span className="h-2 bg-[#e5e0d8] relative overflow-hidden">
            <span
              className="absolute inset-y-0 left-0 bg-[#9a7218] transition-[width] duration-700"
              style={{ width: `${(r.count / m) * 100}%` }}
            />
          </span>
          <span className="font-mono-label text-[10px] text-[#86928a] text-right tabular-nums">
            {r.count}
          </span>
        </li>
      ))}
    </ul>
  );
}

function DailyChart({ daily }) {
  const m = Math.max(...daily.map((d) => d.views), 1);
  return (
    <div data-testid="daily-chart" className="mt-6 border border-[#e5e0d8] bg-white p-6">
      <div className="flex items-end gap-1.5 h-40">
        {daily.map((d) => (
          <div key={d.day} className="flex-1 flex flex-col items-center justify-end h-full group">
            <span className="font-mono-label text-[9px] text-[#86928a] opacity-0 group-hover:opacity-100 transition-opacity mb-1">
              {d.views}
            </span>
            <span
              className="w-full bg-[#14352a] group-hover:bg-[#9a7218] transition-colors"
              style={{ height: `${Math.max((d.views / m) * 100, d.views ? 3 : 1)}%` }}
              title={`${d.day}: ${d.views} views · ${d.visits} visits`}
            />
          </div>
        ))}
      </div>
      <div className="mt-3 flex justify-between font-mono-label text-[9px] text-[#86928a]">
        <span>{daily[0]?.day.slice(5)}</span>
        <span>page views per day</span>
        <span>{daily[daily.length - 1]?.day.slice(5)}</span>
      </div>
    </div>
  );
}
